/**
 * cbb-variants.js
 * --------------------------------------------------------------------------
 * <cbb-variants> custom element. Maps selected option values to a variant ID,
 * updates the form's hidden id input, optionally syncs the URL, and dispatches
 * a `cbb:variant:changed` event so cbb-product-form.js (and Skio) can react.
 *
 * Self-contained. Does not touch globals beyond `customElements`.
 * --------------------------------------------------------------------------
 */
(function () {
  if (customElements.get('cbb-variants')) return;

  class CbbVariants extends HTMLElement {
    constructor() {
      super();
      this._onChange = this._onChange.bind(this);
    }

    connectedCallback() {
      this.variants = this._readVariants();
      this.formId = this.dataset.formId;
      this.shouldUpdateUrl = this.hasAttribute('data-update-url');

      this.addEventListener('change', this._onChange);

      // Initialize availability UI on first paint.
      this._updateAvailability();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
    }

    _readVariants() {
      const tag = this.querySelector('script[data-cbb-variants-json]');
      if (!tag) return [];
      try {
        return JSON.parse(tag.textContent);
      } catch (e) {
        console.warn('[cbb-variants] Could not parse variant JSON', e);
        return [];
      }
    }

    _onChange(event) {
      if (!event.target.matches('[data-cbb-option-input]')) return;
      const variant = this._matchVariant();
      this._updateLegend(event.target);
      this._updateAvailability();
      this._syncFormInput(variant);
      if (this.shouldUpdateUrl && variant) this._syncUrl(variant);
      this._dispatch(variant);
    }

    _selectedValues() {
      const groups = this.querySelectorAll('.cbb-variants__group');
      return Array.from(groups).map((group) => {
        const checked = group.querySelector('[data-cbb-option-input]:checked');
        return checked ? checked.value : null;
      });
    }

    _matchVariant() {
      const selected = this._selectedValues();
      return (
        this.variants.find((v) => {
          return v.options.every((opt, i) => opt === selected[i]);
        }) || null
      );
    }

    _updateLegend(input) {
      const group = input.closest('.cbb-variants__group');
      if (!group) return;
      const display = group.querySelector('[data-cbb-selected-value]');
      if (display) display.textContent = input.value;
    }

    _updateAvailability() {
      // Disable pills whose option-value combo would produce an unavailable variant.
      // Greedy match: if NO available variant exists with this value (given other
      // currently-selected values), mark the pill unavailable.
      const groups = this.querySelectorAll('.cbb-variants__group');
      const selected = this._selectedValues();

      groups.forEach((group, optionIndex) => {
        const inputs = group.querySelectorAll('[data-cbb-option-input]');
        inputs.forEach((input) => {
          const candidateValues = selected.slice();
          candidateValues[optionIndex] = input.value;

          const match = this.variants.find((v) =>
            v.options.every((opt, i) => {
              if (candidateValues[i] == null) return true;
              return opt === candidateValues[i];
            })
          );

          const pill = group.querySelector(`[data-cbb-option-pill][data-value="${cssEscape(input.value)}"]`);
          if (!pill) return;

          if (!match) {
            pill.classList.add('is-unavailable');
            pill.setAttribute('aria-disabled', 'true');
          } else if (!match.available) {
            pill.classList.add('is-soldout');
            pill.classList.remove('is-unavailable');
            pill.removeAttribute('aria-disabled');
          } else {
            pill.classList.remove('is-unavailable', 'is-soldout');
            pill.removeAttribute('aria-disabled');
          }
        });
      });
    }

    _syncFormInput(variant) {
      // Update the form's [data-cbb-variant-input] (preferred) or fall back to
      // any [name="id"] in the same form.
      const form = this.formId ? document.getElementById(this.formId) : this.closest('form');
      if (!form) return;
      const input = form.querySelector('[data-cbb-variant-input]') || form.querySelector('[name="id"]');
      if (!input) return;
      input.value = variant ? variant.id : '';
      // Native change event so subscription apps watching the input update.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _syncUrl(variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    _dispatch(variant) {
      this.dispatchEvent(
        new CustomEvent('cbb:variant:changed', {
          bubbles: true,
          detail: { variant }
        })
      );
    }
  }

  // Minimal CSS.escape polyfill for older Safari.
  function cssEscape(value) {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, (ch) => '\\' + ch);
  }

  customElements.define('cbb-variants', CbbVariants);
})();
